import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Users, Plus, Search, ChevronRight, Stethoscope, Upload, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/axios';
import { formatDate, getInitials } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { NuevoPacienteDialog } from '@/components/pacientes/NuevoPacienteDialog';
import { ImportarPacientesDialog } from '@/components/pacientes/ImportarPacientesDialog';

const ROLES_CREAR: string[] = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.ASISTENTE];
const PAGE_SIZE = 30;

interface PacientesPagina {
  data: any[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export function PacientesPage() {
  const navigate = useNavigate();
  const { usuario } = useAuthStore();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [openImportar, setOpenImportar] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const canCreate = usuario?.rolId && ROLES_CREAR.includes(usuario.rolId);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['pacientes', search],
    queryFn: ({ pageParam }) =>
      api.get('/api/pacientes', { params: { search: search || undefined, page: pageParam, limit: PAGE_SIZE } })
        .then(r => r.data as PacientesPagina),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined),
  });

  const pacientes = useMemo(() => data?.pages.flatMap(p => p.data) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) fetchNextPage(); },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, pacientes.length]);

  const { data: consultasActivas = [] } = useQuery({
    queryKey: ['consultas-activas'],
    queryFn: () => api.get('/api/consultas').then(r => r.data as any[]),
    refetchInterval: 30_000,
  });

  const enConsultaMap = useMemo(() => {
    const map: Record<string, string> = {};
    (consultasActivas as any[])
      .filter(c => c.estado === 'abierta' || c.estado === 'en_curso')
      .forEach(c => { map[c.pacienteId] = c.medico?.nombre || 'Médico'; });
    return map;
  }, [consultasActivas]);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-text-primary">Pacientes</h2>
          <p className="text-sm text-text-secondary mt-0.5">{total} paciente{total !== 1 ? 's' : ''} registrado{total !== 1 ? 's' : ''}</p>
        </div>
        {/* Botones desktop */}
        {canCreate && (
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setOpenImportar(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-text-primary rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <Upload size={16} /> Importar Excel
            </button>
            <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
              <Plus size={16} /> Nuevo paciente
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input type="text" placeholder="Buscar por nombre..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-text-secondary">Cargando pacientes...</div>
      ) : pacientes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Users size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-text-secondary font-medium">Sin pacientes encontrados</p>
          {canCreate && <p className="text-sm text-text-secondary mt-1">Registra el primer paciente para comenzar</p>}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
          {pacientes.map((p: any) => (
            <div key={p.id} className="flex items-center gap-4 px-4 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => navigate(`/pacientes/${p.id}`)}>
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">
                {getInitials(`${p.nombre} ${p.apellidoPaterno}`)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-text-primary">{p.nombre} {p.apellidoPaterno} {p.apellidoMaterno}</p>
                  {enConsultaMap[p.id] && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-[10px] font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                      <Stethoscope size={9} />
                      {enConsultaMap[p.id]}
                    </span>
                  )}
                </div>
                <div className="flex gap-3 mt-0.5">
                  {p.fechaNacimiento && <span className="text-xs text-text-secondary">{formatDate(p.fechaNacimiento)}</span>}
                  {p.genero && <span className="text-xs text-text-secondary capitalize">{p.genero.replace('_', ' ')}</span>}
                  {p.telefono && <span className="text-xs text-text-secondary">{p.telefono}</span>}
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
            </div>
          ))}
          <div ref={sentinelRef} className="h-1" />
          {isFetchingNextPage && (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-text-secondary">
              <Loader2 size={16} className="animate-spin" /> Cargando más pacientes...
            </div>
          )}
        </div>
      )}

      <NuevoPacienteDialog open={open} onOpenChange={setOpen} />
      <ImportarPacientesDialog open={openImportar} onOpenChange={setOpenImportar} />

      {/* FAB mobile */}
      {canCreate && (
        <button
          onClick={() => setOpen(true)}
          className="md:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center hover:bg-primary-600 transition-colors"
          style={{ boxShadow: '0 4px 16px rgba(14,133,191,0.4)' }}
          aria-label="Nuevo paciente"
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
